/**
 * Formato de fechas y textos para mostrar en pantalla (español de Chile).
 */

const ZONA_CHILE = 'America/Santiago';

/*
 * Se arma con `formatToParts` y no con `toLocaleString`: según el navegador, es-CL da «28-oct» o
 * «02:09 p. m.», y eso partía la fecha en dos líneas. Así siempre sale «28 oct» y «28 oct, 14:09».
 */
const FORMATO_DIA = new Intl.DateTimeFormat('es-CL', {
  timeZone: ZONA_CHILE,
  day: 'numeric',
  month: 'short',
});
const FORMATO_HORA = new Intl.DateTimeFormat('es-CL', {
  timeZone: ZONA_CHILE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function partes(formato: Intl.DateTimeFormat, fecha: Date): Partial<Record<string, string>> {
  return Object.fromEntries(formato.formatToParts(fecha).map((p) => [p.type, p.value]));
}

function aFecha(iso: string | null | undefined): Date | null {
  const fecha = iso ? new Date(iso) : null;
  return fecha && !Number.isNaN(fecha.getTime()) ? fecha : null;
}

/** «28 oct», en hora de Chile. Vacío si la fecha no es válida. */
export function fechaCorta(iso: string | null | undefined): string {
  const fecha = aFecha(iso);
  if (!fecha) return '';
  const { day = '', month = '' } = partes(FORMATO_DIA, fecha);
  return `${day} ${month.replaceAll('.', '')}`;
}

/** «28 oct, 14:09» (24 horas, hora de Chile). Vacío si la fecha no es válida. */
export function fechaHora(iso: string | null | undefined): string {
  const fecha = aFecha(iso);
  if (!fecha) return '';
  const { hour = '', minute = '' } = partes(FORMATO_HORA, fecha);
  return `${fechaCorta(iso)}, ${hour}:${minute}`;
}

/** Día calendario (`AAAA-MM-DD`) de un instante, en hora de Chile. Vacío si no es válido. */
export function diaEnChile(instante: string | Date | null | undefined): string {
  const fecha = typeof instante === 'string' ? new Date(instante) : instante;
  if (!fecha || Number.isNaN(fecha.getTime())) return '';
  // `en-CA` da justo el formato AAAA-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_CHILE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(fecha);
}

/** «18 oct 2026», en hora de Chile (no depende de la zona del computador). Vacío si no es válida. */
export function fechaDiaChile(iso: string | null | undefined): string {
  const fecha = iso ? new Date(iso) : null;
  if (!fecha || Number.isNaN(fecha.getTime())) return '';
  return fecha
    .toLocaleDateString('es-CL', {
      timeZone: ZONA_CHILE,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replaceAll('.', '');
}

/*
 * Un día suelto (`AAAA-MM-DD`, como la fecha límite) no tiene hora: se formatea como el mediodía UTC de ese día,
 * que en Chile (UTC-3/-4) sigue siendo el mismo día. Pasar la medianoche UTC lo correría al día anterior.
 */
const mediodiaUtc = (dia: string) => `${dia}T12:00:00.000Z`;

/** «12 nov» para un día `AAAA-MM-DD`. Vacío si no es válido. */
export const diaCorto = (dia: string | null | undefined): string =>
  dia ? fechaCorta(mediodiaUtc(dia)) : '';

/** «12 nov 2026» para un día `AAAA-MM-DD`. Vacío si no es válido. */
export const diaLargo = (dia: string | null | undefined): string =>
  dia ? fechaDiaChile(mediodiaUtc(dia)) : '';

/** «850 B», «12 KB», «3,4 MB». */
export function formatearTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toLocaleString('es-CL', { maximumFractionDigits: 1 })} MB`;
}

/** Nombre para mostrar de una persona opcional. */
export const nombreOSinAsignar = (persona: { nombre: string } | null | undefined): string =>
  persona?.nombre ?? 'Sin asignar';
